import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageShell from '../../../components/layout/PageShell';
import { fetchNearbyDiscovery } from '../../../services/discoveryService';
import useBrowserLocation from '../hooks/useBrowserLocation';
import LocationPromptCard from '../components/LocationPromptCard';
import NearbyMatchCard from '../components/NearbyMatchCard';
import NearbyTournamentCard from '../components/NearbyTournamentCard';
import './nearby-discovery.css';

const RADIUS_OPTIONS = [5, 10, 25, 50];
const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'live', label: 'Live only' },
  { value: 'upcoming', label: 'Upcoming only' },
  { value: 'tournaments', label: 'Tournaments only' },
];

const DiscoverySection = ({ title, emptyText, items = [], renderCard }) => (
  <section className="d-grid gap-3">
    <div className="d-flex align-items-center justify-content-between gap-2">
      <h2 className="h5 mb-0">{title}</h2>
      <span className="text-muted small">{items.length} found</span>
    </div>

    {items.length === 0 ? <div className="alert alert-light border nearby-empty-state mb-0">{emptyText}</div> : null}

    {items.length > 0 ? (
      <div className="row g-3">
        {items.map((item) => (
          <div key={item.id} className="col-12 col-md-6 col-xl-4">
            {renderCard(item)}
          </div>
        ))}
      </div>
    ) : null}
  </section>
);

const NearbyDiscoveryPage = () => {
  const { coords, isLocating, locationError, hasRequestedLocation, requestLocation } = useBrowserLocation();
  const [radiusKm, setRadiusKm] = useState(25);
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [discovery, setDiscovery] = useState({
    liveMatches: [],
    upcomingMatches: [],
    ongoingTournaments: [],
    upcomingTournaments: [],
  });

  useEffect(() => {
    if (!coords?.latitude || !coords?.longitude) return undefined;

    const abortController = new AbortController();

    const loadDiscovery = async () => {
      setIsLoading(true);
      setRequestError('');

      try {
        const payload = await fetchNearbyDiscovery({
          latitude: coords.latitude,
          longitude: coords.longitude,
          radiusKm,
          limit: 8,
          signal: abortController.signal,
        });
        setDiscovery(payload);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setRequestError('Could not load nearby cricket activity right now. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadDiscovery();
    return () => abortController.abort();
  }, [coords?.latitude, coords?.longitude, radiusKm]);

  const visible = useMemo(() => {
    if (activeFilter === 'live') {
      return { ...discovery, upcomingMatches: [], ongoingTournaments: [], upcomingTournaments: [] };
    }

    if (activeFilter === 'upcoming') {
      return { ...discovery, liveMatches: [], ongoingTournaments: [], upcomingTournaments: discovery.upcomingTournaments };
    }

    if (activeFilter === 'tournaments') {
      return { ...discovery, liveMatches: [], upcomingMatches: [] };
    }

    return discovery;
  }, [activeFilter, discovery]);

  return (
    <PageShell>
      <div className="d-grid gap-4 nearby-discovery-page">
        {!coords ? (
          <LocationPromptCard isLocating={isLocating} locationError={locationError} onRequestLocation={requestLocation} />
        ) : (
          <section className="card border-0 shadow-sm nearby-hero-card">
            <div className="card-body p-4 d-grid gap-3">
              <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-lg-between gap-3">
                <div>
                  <p className="eyebrow text-primary mb-1">Nearby discovery</p>
                  <h1 className="h4 mb-1">Cricket near your current location</h1>
                  <p className="text-muted mb-0">Using {coords.latitude.toFixed(4)}, {coords.longitude.toFixed(4)}</p>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  {RADIUS_OPTIONS.map((radius) => (
                    <button
                      key={radius}
                      type="button"
                      className={`btn btn-sm ${radiusKm === radius ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setRadiusKm(radius)}
                    >
                      {radius} km
                    </button>
                  ))}
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={requestLocation} disabled={isLocating}>
                    Retry location
                  </button>
                </div>
              </div>

              <div className="d-flex gap-2 flex-wrap" role="group" aria-label="Nearby discovery filters">
                {FILTER_OPTIONS.map((filter) => (
                  <button
                    key={filter.value}
                    type="button"
                    className={`btn btn-sm ${activeFilter === filter.value ? 'btn-dark' : 'btn-outline-dark'}`}
                    onClick={() => setActiveFilter(filter.value)}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {hasRequestedLocation && !coords && locationError ? (
          <div className="text-center">
            <button type="button" className="btn btn-outline-primary" onClick={requestLocation} disabled={isLocating}>
              Try again
            </button>
          </div>
        ) : null}

        {coords ? (
          <>
            {isLoading ? <div className="alert alert-info mb-0">Loading nearby cricket experiences…</div> : null}
            {requestError ? <div className="alert alert-warning mb-0">{requestError}</div> : null}

            {!isLoading && !requestError ? (
              <>
                <DiscoverySection
                  title="Nearby Live Matches"
                  emptyText="No live matches near you right now."
                  items={visible.liveMatches}
                  renderCard={(item) => <NearbyMatchCard match={item} isLive />}
                />

                <DiscoverySection
                  title="Nearby Upcoming Matches"
                  emptyText="No upcoming matches found in this radius yet."
                  items={visible.upcomingMatches}
                  renderCard={(item) => <NearbyMatchCard match={item} />}
                />

                <DiscoverySection
                  title="Nearby Ongoing Tournaments"
                  emptyText="No ongoing tournaments near you right now."
                  items={visible.ongoingTournaments}
                  renderCard={(item) => <NearbyTournamentCard tournament={item} isOngoing />}
                />

                <DiscoverySection
                  title="Nearby Upcoming Tournaments"
                  emptyText="No nearby tournaments found."
                  items={visible.upcomingTournaments}
                  renderCard={(item) => <NearbyTournamentCard tournament={item} />}
                />
              </>
            ) : null}
          </>
        ) : (
          <div className="text-center text-muted small">
            Want quick access later? Open this from your dashboard: <Link to="/dashboard/individual/nearby">Nearby Discovery</Link>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default NearbyDiscoveryPage;
